import { useState, useCallback, useRef, useEffect } from 'react';
import { getDatabase, ref, get } from 'firebase/database';

interface UserCache {
    [uid: string]: string | null;
}

/**
 * Custom hook for fetching and caching user display names
 *
 * This hook provides functions to fetch user names from Firebase and caches them
 * to avoid redundant database calls. It supports both individual lookups and bulk loading.
 *
 * Performance optimizations:
 * - Uses ref-based cache lookup to avoid stale closures
 * - Tracks in-flight requests to prevent duplicate fetches
 * - Parallel fetches for bulk loading
 *
 * @returns Object containing:
 *  - userNames: Record of uid -> display name
 *  - getUserName: Function to get a single user's name (async)
 *  - loadUserNames: Function to load multiple users' names (async)
 *  - loadAllUsers: Function to load the entire user list (async)
 */
export const useUserNames = () => {
    const [userNames, setUserNames] = useState<UserCache>({});
    const db = getDatabase();

    // Use ref for cache lookup to avoid stale closures
    const userNamesRef = useRef<UserCache>({});

    // Keep ref in sync with state
    useEffect(() => {
        userNamesRef.current = userNames;
    }, [userNames]);

    // Track in-flight requests to prevent duplicate fetches
    const pendingRequests = useRef<Map<string, Promise<string | null>>>(new Map());

    /**
     * Get a single user's display name
     * Checks cache first, then fetches from database if not cached
     *
     * @param uid - User ID to fetch
     * @returns Promise resolving to the user's display name or 'Okänd användare'
     */
    const getUserName = useCallback(async (uid: string | undefined): Promise<string> => {
        if (!uid) return 'Okänd användare';

        // Check cache using ref (always current)
        const cached = userNamesRef.current[uid];
        if (cached !== undefined) {
            return cached || 'Okänd användare';
        }

        // Check for in-flight request
        const pending = pendingRequests.current.get(uid);
        if (pending) {
            const result = await pending;
            return result || 'Okänd användare';
        }

        // Create new request
        const fetchPromise = (async () => {
            try {
                const userRef = ref(db, `users/userList/${uid}/name`);
                const snapshot = await get(userRef);
                const name = snapshot.exists() ? snapshot.val() : null;

                setUserNames(prev => ({ ...prev, [uid]: name }));
                return name;
            } catch (error) {
                console.error('Error fetching user name:', error);
                setUserNames(prev => ({ ...prev, [uid]: null }));
                return null;
            } finally {
                pendingRequests.current.delete(uid);
            }
        })();

        pendingRequests.current.set(uid, fetchPromise);
        const result = await fetchPromise;
        return result || 'Okänd användare';
    }, [db]);

    /**
     * Load multiple users' names at once using parallel fetches
     * More efficient than calling getUserName multiple times
     *
     * @param uids - Array of user IDs to fetch
     */
    const loadUserNames = useCallback(async (uids: string[]): Promise<void> => {
        // Filter to only uncached UIDs using ref for current values
        const uncachedUids = uids.filter(uid => userNamesRef.current[uid] === undefined);

        if (uncachedUids.length === 0) return;

        // For many users, batch load all users at once (more efficient)
        if (uncachedUids.length > 10) {
            await loadAllUsersInternal();
            return;
        }

        try {
            // Parallel fetches for small batches
            const fetchPromises = uncachedUids.map(async (uid) => {
                const userRef = ref(db, `users/userList/${uid}/name`);
                const snapshot = await get(userRef);
                return { uid, name: snapshot.exists() ? snapshot.val() : null };
            });

            const results = await Promise.all(fetchPromises);

            const newNames: UserCache = {};
            results.forEach(({ uid, name }) => {
                newNames[uid] = name;
            });

            setUserNames(prev => ({ ...prev, ...newNames }));
        } catch (error) {
            console.error('Error loading user names:', error);
        }
    }, [db]);

    /**
     * Internal function to load all users
     */
    const loadAllUsersInternal = useCallback(async (): Promise<void> => {
        try {
            const usersRef = ref(db, 'users/userList');
            const usersSnapshot = await get(usersRef);

            if (usersSnapshot.exists()) {
                const usersData = usersSnapshot.val() as Record<string, { name?: string }>;
                const names: Record<string, string | null> = {};

                Object.entries(usersData).forEach(([userId, userData]) => {
                    names[userId] = userData?.name || null;
                });

                setUserNames(prev => ({
                    ...prev,
                    ...names
                }));
            }
        } catch (error) {
            console.error('Error loading all users:', error);
        }
    }, [db]);

    /**
     * Load the entire user list from users/userList
     * Most efficient when you need to display many users
     */
    const loadAllUsers = loadAllUsersInternal;

    return {
        userNames,
        getUserName,
        loadUserNames,
        loadAllUsers
    };
};
